import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import type { api } from 'api';
import type { HTTPError } from 'api/error';
import type {
  CreateBranchPayload,
  CreateBranchResponse,
} from 'api/resources/draft/types';
import { curryPostFunction } from 'api/utils';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { getBranchesQueryKey } from './useBranches';

type FunctionArgs = Omit<CreateBranchPayload, 'accountId' | 'projectId'>;

const useCreateDraft = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof api.createBranch>>,
    HTTPError,
    FunctionArgs
  >,
  overrideProjectId?: string,
) => {
  const api = useApi();
  const toast = useToast();

  const { accountId, projectId } = useProjectRoute();

  const projectIdToUse = overrideProjectId ?? projectId;
  const branchesQueryKey = getBranchesQueryKey(accountId, projectId);

  return useMutation<CreateBranchResponse, HTTPError, FunctionArgs>(
    curryPostFunction(api.methods.createBranch)({
      accountId,
      projectId: projectIdToUse,
    }),
    {
      retry: false,
      onError: () => {
        toast.error({
          title: 'Error creating branch',
          description:
            'There was an error creating the branch, please refresh the page and try again.',
        });
      },
      meta: { invalidates: [branchesQueryKey] },

      ...options,
    },
  );
};

export default useCreateDraft;
